import { useRef, useState } from 'react'
import { v4 as uuidV4 } from 'uuid'
import { Typography, TableRow, TableCell } from '@mui/material'
import { ActionText } from 'components/atoms/Text'
import { Add } from '@mui/icons-material'
import ExcelJS from 'exceljs'
import { validationsRules } from 'utils/formValidations'
import CommonDialog from 'components/molecules/notificationOverlay/CommonDialog'
import { useTranslation } from 'react-i18next'
import RowStack from '../container/RowStack'
import { ColStack } from '../container/InCardStack'
import PresetTableContainer from 'components/atoms/table/PresetTableContainer'

const EmailsImportInput = ({ emails, setEmails }) => {
  const { t } = useTranslation()
  const [total, setTotal] = useState(0)
  const [validTotal, setValidTotal] = useState(0)
  const [invalidInvites, setInvalidInvites] = useState([])
  const [openDialog, setOpenDialog] = useState(false)
  const fileInputRef = useRef(null)

  const handleFileChange = event => {
    setInvalidInvites([])
    setTotal(0)
    setValidTotal(0)
    const file = event.target.files[0]
    if (!file) return
    if (file.type === 'text/csv') {
      handleCSV(file)
    } else if (
      file.type ===
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      file.name.endsWith('.xlsx')
    ) {
      handleExcel(file)
    } else {
      return
    }
    // Reset the input value after reading the file
    event.target.value = null
  }

  const handleCSV = file => {
    const reader = new FileReader()
    reader.onload = e => {
      const content = e.target.result
      let rows = content.split('\n')
      const headerRow = rows[0].split(',').map(header => header.trim())
      rows = rows.slice(1).map(row => row.split(',').map(ele => ele.trim()))
      setInviteData(headerRow, rows)
    }
    reader.readAsText(file)
  }

  const handleExcel = file => {
    const reader = new FileReader()
    const workbook = new ExcelJS.Workbook()

    reader.onload = async e => {
      const content = e.target.result
      try {
        await workbook.xlsx.load(content)
        workbook.eachSheet(sheet => {
          const headerRow = []
          const rows = []
          sheet.eachRow((row, rowNumber) => {
            const rowData = []
            row.eachCell({ includeEmpty: false }, cell => {
              if (rowNumber === 1) headerRow.push(cell.value)
              else rowData.push(cell.value)
            })
            if (rowData.length > 0) rows.push(rowData)
          })
          setInviteData(headerRow, rows)

          // Break the loop after processing the first sheet
          return
        })
      } catch (error) {
        return
      }
    }
    reader.readAsArrayBuffer(file)
  }

  const dataHeaders = [
    { key: 'email', label: 'Leader Email' },
    { key: 'firstName', label: 'Leader First Name' },
    { key: 'lastName', label: 'Leader Last Name' },
    { key: 'job', label: 'Role' },
    { key: 'department', label: 'Department' }
  ]

  const setInviteData = async (headerRow, rows) => {
    const validData = []
    const invalidData = []
    for (let i = 0; i < rows.length; i++) {
      const values = rows[i]
      const csvInvites = {}
      let validEmail = false
      for (let j = 0; j < headerRow.length; j++) {
        const header = headerRow[j]
        const value = values[j] ? values[j].toString().trim() : ''
        if (header && value) {
          // Map headers to desired keys
          const headerObj = dataHeaders.find(h => h.label === header)
          if (headerObj) {
            if (headerObj.key === 'email') {
              validEmail = await validateEmail(value)
            }
            csvInvites[headerObj.key] = value
          }
        }
      }
      // Check object not empty
      if (Object.keys(csvInvites).length === 0) continue
      // Push to valid or invalid data based on email validation
      if (validEmail) {
        validData.push(csvInvites)
        setValidTotal(prev => prev + 1)
      } else invalidData.push(csvInvites)
      setTotal(prev => prev + 1)
    }

    let newEmails = [...emails, ...validData]
    setEmails(newEmails)
    setInvalidInvites(invalidData)
    setOpenDialog(true)
  }

  const validateEmail = async email => {
    try {
      await Promise.resolve(validationsRules.email.validate(email))
      const existing = emails.find(
        e => e.email.toLowerCase() === email.toLowerCase()
      )
      if (existing) throw new Error('Email already exists')
      return true
    } catch (error) {
      return false
    }
  }

  const handleImportClick = () => {
    fileInputRef.current.click()
  }

  return (
    <>
      <ActionText
        display='flex'
        justifyContent='center'
        width='fit-content'
        onClick={() => handleImportClick()}>
        <Add color='primary' /> Import
      </ActionText>

      <input
        type='file'
        ref={fileInputRef}
        accept='.csv, .xlsx'
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      <CommonDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        titleText={t('company.importSummary')}
        notificationText={
          invalidInvites.length > 0 && t('company.importSummaryNote')
        }
        confirmText={t('common.ok')}
        showCloseButton={true}
        maxWidth='xl'
        bodyWidth={invalidInvites.length > 0 ? 800 : undefined}>
        <ColStack spacing={12}>
          <RowStack>
            <Typography>
              {t('company.importSuccessFailed', {
                success: validTotal,
                total
              })}
            </Typography>
            <Typography>
              {t('company.totalInvitesFailed', {
                count: invalidInvites.length
              })}
            </Typography>
          </RowStack>
          {invalidInvites.length > 0 && (
            <ColStack spacing={0}>
              <Typography variant='h5'>{t('company.failedInvites')}</Typography>
              <PresetTableContainer
                minHeight={'auto'}
                headers={
                  <>
                    {dataHeaders.map(header => (
                      <TableCell key={header.key}>{header.label}</TableCell>
                    ))}
                  </>
                }>
                {invalidInvites.map(i => {
                  return (
                    <TableRow key={uuidV4()}>
                      {dataHeaders.map(header => (
                        <TableCell key={header.key}>
                          {i[header.key] || ''}
                        </TableCell>
                      ))}
                    </TableRow>
                  )
                })}
              </PresetTableContainer>
            </ColStack>
          )}
        </ColStack>
      </CommonDialog>
    </>
  )
}

export default EmailsImportInput
